// Shared "soft tonal + status dot" styling for all status pills
// (StatusBadge, StatusPill, SourceBadge). One source of truth so the pills
// stay visually unified and adapt cleanly to both light and dark themes.

export const STATUS_PILL_BASE =
  'inline-flex items-center gap-[5px] rounded-full px-2 py-[3px] text-[10.5px] font-semibold tracking-[0.04em] uppercase whitespace-nowrap select-none';

// pill: neutral text only — no background. dot carries the semantic color.
export const STATUS_SCHEMES = {
  emerald: { pill: 'text-muted-foreground', dot: 'bg-ds-success' },
  amber: { pill: 'text-muted-foreground', dot: 'bg-ds-warning' },
  red: { pill: 'text-muted-foreground', dot: 'bg-ds-danger' },
  teal: { pill: 'text-muted-foreground', dot: 'bg-teal-500' },
  indigo: { pill: 'text-muted-foreground', dot: 'bg-indigo-500' },
  violet: { pill: 'text-muted-foreground', dot: 'bg-violet-500' },
  blue: { pill: 'text-muted-foreground', dot: 'bg-blue-500' },
  orange: { pill: 'text-muted-foreground', dot: 'bg-orange-500' },
  slate: { pill: 'text-muted-foreground', dot: 'bg-slate-400' },
  gray: { pill: 'text-muted-foreground', dot: 'bg-gray-400' },
};

export const DEFAULT_STATUS_SCHEME = 'gray';

/** Resolve a scheme name to its class set, falling back to the neutral scheme. */
export function getStatusScheme(name) {
  return STATUS_SCHEMES[name] ?? STATUS_SCHEMES[DEFAULT_STATUS_SCHEME];
}

// Soft-tonal BUTTON treatment per scheme — a tinted background at rest, a
// touch deeper on hover, text colored to match, and a matching border so
// the button reads as a real bordered control rather than a flat patch of
// color. Same scheme names as STATUS_SCHEMES above (one shared hue
// vocabulary for the whole app, whether it's coloring a status dot or an
// action button) but with real background-safe swatches instead of just a
// dot color. Reuses the ds-* tokens for the 3 schemes that have them
// (emerald/amber/red); the rest fall back to the matching Tailwind palette
// shade pair, same precedent AssignmentPipeline/tabs/tabFormatters.js's
// SCHEME_BG_CLASS/SCHEME_TEXT_CLASS already set for status chips. `gray` is
// the literal fallback for anything uncategorized — keeps today's plain
// neutral outline look rather than erroring or looking unstyled.
//
// Pair these with `variant="tone"` (src/components/ui/button.jsx), not
// `outline`/`outlineV2` — those variants bake in their own hover:text-primary/
// hover:bg-brand-bg-subtle, and Button's buttonVariants({variant, size,
// className}) merges className via cva's own clsx (not tailwind-merge), so
// a caller's className can't reliably override those baked-in classes.
// Confirmed live: every tone's hover text was coming out primary-blue
// regardless of this file's own hoverBg, because the outline variant's
// hover:text-primary kept winning. `variant="tone"` carries no color
// opinions of its own, so there's nothing left to fight.
//
// Every entry carries an explicit `dark:` variant for bg/text/border/hover —
// confirmed live this was missing entirely at first (the plain Tailwind
// `-50`/`-700`/`-200` shade pairs used for the 6 non-ds schemes have no dark
// counterpart on their own), which made e.g. Assign Slot's violet tone
// render as a bright pale-lavender patch against the dark theme's navy
// background. `emerald`/`amber`/`red`'s own bg/text already ride on ds-*
// tokens that index.css's `.dark` block redefines — text uses the plain
// `ds-success`/`ds-warning`/`ds-danger` token (not `-deep`), since `-deep`
// isn't redefined for dark but the plain token is (and is identical to
// `-deep` in light mode, so this is a same-visual, better-adapted swap, not
// a trade-off) — only each scheme's hover/border needed real dark: classes
// added, via plain rgba() arbitrary values matching index.css's own dark
// `-subtle` treatment (a translucent tint over the dark background) rather
// than guessing at a Tailwind shade.
export const TONE_BUTTON_CLASSES = {
  emerald: {
    bg: 'bg-ds-success-subtle',
    text: 'text-ds-success',
    border: 'border-emerald-200 dark:border-emerald-800/50',
    hoverBg: 'hover:bg-ds-success-tint dark:hover:bg-[rgba(52,211,153,0.25)]',
    // Solid fill — per explicit request/reference, used only for the
    // primary row/bulk-bar button (GenericFlowAction's `button` branch),
    // never the kebab menu item (a solid-green row would look out of place
    // next to plain tonal-text menu items). Plain Tailwind emerald rather
    // than the ds-success token on purpose: a solid fill's own background
    // doesn't depend on the page/card background the way a translucent tint
    // does, so it reads fine on both themes without any dark: override —
    // sidesteps the exact light/dark adaptation problem the tonal treatment
    // above needs so much care for.
    solid: {
      bg: 'bg-emerald-600',
      text: 'text-white',
      hoverBg: 'hover:bg-emerald-700',
    },
  },
  amber: {
    bg: 'bg-ds-warning-subtle',
    text: 'text-ds-warning',
    border: 'border-amber-200 dark:border-amber-800/50',
    hoverBg: 'hover:bg-ds-warning-tint dark:hover:bg-[rgba(251,191,36,0.25)]',
  },
  red: {
    bg: 'bg-ds-danger-subtle',
    text: 'text-ds-danger',
    border: 'border-red-200 dark:border-red-800/50',
    hoverBg: 'hover:bg-red-100 dark:hover:bg-[rgba(248,113,113,0.25)]',
  },
  teal: {
    bg: 'bg-teal-50 dark:bg-teal-950/40',
    text: 'text-teal-700 dark:text-teal-300',
    border: 'border-teal-200 dark:border-teal-800/50',
    hoverBg: 'hover:bg-teal-100 dark:hover:bg-teal-900/50',
  },
  indigo: {
    bg: 'bg-indigo-50 dark:bg-indigo-950/40',
    text: 'text-indigo-700 dark:text-indigo-300',
    border: 'border-indigo-200 dark:border-indigo-800/50',
    hoverBg: 'hover:bg-indigo-100 dark:hover:bg-indigo-900/50',
  },
  violet: {
    bg: 'bg-violet-50 dark:bg-violet-950/40',
    text: 'text-violet-700 dark:text-violet-300',
    border: 'border-violet-200 dark:border-violet-800/50',
    hoverBg: 'hover:bg-violet-100 dark:hover:bg-violet-900/50',
  },
  blue: {
    bg: 'bg-blue-50 dark:bg-blue-950/40',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-200 dark:border-blue-800/50',
    hoverBg: 'hover:bg-blue-100 dark:hover:bg-blue-900/50',
  },
  orange: {
    bg: 'bg-orange-50 dark:bg-orange-950/40',
    text: 'text-orange-700 dark:text-orange-300',
    border: 'border-orange-200 dark:border-orange-800/50',
    hoverBg: 'hover:bg-orange-100 dark:hover:bg-orange-900/50',
  },
  slate: {
    bg: 'bg-slate-50 dark:bg-slate-800/40',
    text: 'text-slate-600 dark:text-slate-300',
    border: 'border-slate-200 dark:border-slate-700/50',
    hoverBg: 'hover:bg-slate-100 dark:hover:bg-slate-800/60',
  },
  gray: {
    // Already theme-aware CSS-var-backed tokens (same ones the original
    // `outline` variant used, already proven correct in dark mode app-wide)
    // — no dark: overrides needed here.
    bg: 'bg-card',
    text: 'text-muted-foreground',
    border: 'border-input',
    hoverBg: 'hover:bg-brand-bg-subtle hover:text-primary',
  },
};

/** Resolve a scheme name to its button tone classes, falling back to gray. */
export function getToneButtonClasses(name) {
  return TONE_BUTTON_CLASSES[name] ?? TONE_BUTTON_CLASSES.gray;
}
